import type { FilesListItem } from '../types';
import type Uppy from '@uppy/core';
import type { Body, Meta } from '@uppy/core';

import { useUppyState } from '@uppy/react';
import { useTranslation } from 'react-i18next';
import { Icon } from '~/atoms';

import { FilesList } from './FilesList';

type UppyFileShape = {
  data?: unknown;
  error?: unknown;
  id: string;
  name: string;
  preview?: string;
  progress?: { percentage?: number; uploadComplete?: boolean };
  type?: string;
};

function mapUppyFileToItem(file: UppyFileShape): FilesListItem {
  return {
    data: file.data instanceof Blob ? file.data : undefined,
    error: file.error,
    id: file.id,
    name: file.name,
    preview: file.preview,
    progress: file.progress,
    type: file.type,
  };
}

type P<B extends Body> = {
  readonly isDisabled?: boolean;
  readonly uppy: Uppy<Meta, B>;
} & ComponentStyling;

export function UppyFilesList<B extends Body>({
  className,
  isDisabled,
  style,
  uppy,
}: P<B>) {
  const { t } = useTranslation('ui');

  // Uppy's useUppyState returns untyped state; we map to FilesListItem
  /* oxlint-disable typescript/no-unsafe-assignment, typescript/no-unsafe-argument */
  const files = useUppyState(uppy, (state) => state.files);
  const items: FilesListItem[] = Object.values(files ?? {}).map(
    (file: UppyFileShape) => mapUppyFileToItem(file),
  );
  /* oxlint-enable typescript/no-unsafe-assignment, typescript/no-unsafe-argument */

  return (
    <FilesList
      actions={[
        {
          disabled: isDisabled,
          icon: <Icon metaphor="close" size="sm" />,
          id: 'remove',
          label: t(($) => $.uploader.removeFile),
          onClick: (item) => uppy.removeFile(item.id),
        },
      ]}
      className={className}
      items={items}
      style={style}
    />
  );
}
